import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, LogOut, Package, ShoppingBag, Star, Download } from "lucide-react";

const EMPTY_PRODUCT = {
    slug: "", name: "", category: "chaines", short_description: "", description: "",
    material: "Argent 925 · Plaqué or 14kt · Moissanite certifiée GRA", images: [], gra_certificate: "",
    variants: [], featured: false, active: true, availability: "stock",
};

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [tab, setTab] = useState("produits");
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [editing, setEditing] = useState(null);
    const [uploading, setUploading] = useState(false);

    const load = useCallback(async () => {
        try {
            const [p, o, r] = await Promise.all([
                api.get("/admin/products"),
                api.get("/admin/orders"),
                api.get("/admin/reviews"),
            ]);
            setProducts(p.data);
            setOrders(o.data);
            setReviews(r.data);
        } catch (e) {
            if (e.response?.status === 401) {
                localStorage.removeItem("vvs_admin_token");
                navigate("/admin/login");
            }
        }
    }, [navigate]);

    useEffect(() => { load(); }, [load]);

    const logout = () => {
        localStorage.removeItem("vvs_admin_token");
        navigate("/admin/login");
    };

    const upload = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            return r.data.path;
        } catch {
            toast.error("Upload échoué");
            return null;
        } finally {
            setUploading(false);
        }
    };

    const saveProduct = async (e) => {
        e.preventDefault();
        try {
            if (editing.id) {
                await api.put(`/admin/products/${editing.id}`, editing);
            } else {
                await api.post("/admin/products", editing);
            }
            toast.success("Produit sauvegardé");
            setEditing(null);
            load();
        } catch {
            toast.error("Erreur sauvegarde");
        }
    };

    const deleteProduct = async (id) => {
        if (!window.confirm("Supprimer ce produit ?")) return;
        await api.delete(`/admin/products/${id}`);
        toast.success("Supprimé");
        load();
    };

    const addVariant = () => {
        setEditing((p) => ({
            ...p,
            variants: [...(p.variants || []), { id: crypto.randomUUID(), sku: "", model: "", carats: "", length_cm: "", price_cad: 0, stock: 0 }],
        }));
    };

    const updateVariant = (i, k, v) => {
        setEditing((p) => {
            const vs = [...p.variants];
            vs[i] = { ...vs[i], [k]: k === "price_cad" || k === "stock" ? Number(v) : v };
            return { ...p, variants: vs };
        });
    };

    const updateOrder = async (order, status, tracking_number, tracking_url) => {
        try {
            await api.put(`/admin/orders/${order.id}`, { status, tracking_number, tracking_url });
            toast.success("Commande mise à jour");
            load();
        } catch {
            toast.error("Erreur");
        }
    };

    const approveReview = async (id) => {
        await api.put(`/admin/reviews/${id}/approve`);
        toast.success("Avis approuvé");
        load();
    };

    const deleteReview = async (id) => {
        await api.delete(`/admin/reviews/${id}`);
        toast.success("Avis supprimé");
        load();
    };

    const exportCsv = async () => {
        try {
            const r = await api.get("/admin/orders/export", { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "orders.csv";
            a.click();
        } catch {
            toast.error("Export échoué");
        }
    };

    const stats = {
        products: products.length,
        orders: orders.length,
        revenue: orders.filter((o) => o.payment_status === "paid").reduce((s, o) => s + o.total_cad, 0),
        pendingReviews: reviews.filter((r) => !r.approved).length,
    };

    return (
        <div className="min-h-screen bg-[#0A0A0C]" data-testid="admin-dashboard">
            <header className="glass-header sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <div className="font-serif-luxe text-lg tracking-[0.2em] chrome-text">VVS HÉRITAGE · ADMIN</div>
                    <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 hover:text-white">
                        <LogOut className="w-4 h-4" /> Déconnexion
                    </button>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="glass-card rounded-2xl p-5"><Package className="w-5 h-5 text-slate-400 mb-2" /><div data-testid="stat-products" className="text-2xl font-serif-luxe">{stats.products}</div><div className="text-xs text-slate-500 uppercase tracking-widest">Produits</div></div>
                    <div className="glass-card rounded-2xl p-5"><ShoppingBag className="w-5 h-5 text-slate-400 mb-2" /><div data-testid="stat-orders" className="text-2xl font-serif-luxe">{stats.orders}</div><div className="text-xs text-slate-500 uppercase tracking-widest">Commandes</div></div>
                    <div className="glass-card rounded-2xl p-5"><div className="text-xs text-slate-400 mb-2">CA</div><div data-testid="stat-revenue" className="text-2xl font-serif-luxe">{stats.revenue.toFixed(0)} $</div><div className="text-xs text-slate-500 uppercase tracking-widest">Payé</div></div>
                    <div className="glass-card rounded-2xl p-5"><Star className="w-5 h-5 text-slate-400 mb-2" /><div data-testid="stat-pending-reviews" className="text-2xl font-serif-luxe">{stats.pendingReviews}</div><div className="text-xs text-slate-500 uppercase tracking-widest">Avis à modérer</div></div>
                </div>

                <div className="flex gap-2 mb-8 border-b border-white/10">
                    {[["produits", "Produits"], ["commandes", "Commandes"], ["avis", "Avis"]].map(([k, l]) => (
                        <button
                            key={k}
                            data-testid={`admin-tab-${k}`}
                            onClick={() => setTab(k)}
                            className={`px-4 py-3 text-xs uppercase tracking-widest transition-colors ${tab === k ? "text-white border-b-2 border-white" : "text-slate-500 hover:text-slate-300"}`}
                        >{l}</button>
                    ))}
                </div>

                {tab === "produits" && (
                    <div data-testid="admin-products-panel">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="font-serif-luxe text-2xl">Produits</h2>
                            <button data-testid="admin-new-product-btn" onClick={() => setEditing({ ...EMPTY_PRODUCT })} className="btn-primary !py-2 !px-4 text-xs">
                                <Plus className="w-4 h-4 mr-1" /> Nouveau
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {products.map((p) => (
                                <div key={p.id} data-testid={`admin-product-${p.slug}`} className="glass-card rounded-2xl p-4 flex gap-4">
                                    <img src={fileUrl(p.images?.[0])} alt="" className="w-20 h-20 object-cover rounded-lg bg-black/40" />
                                    <div className="flex-1">
                                        <div className="font-medium text-slate-100">{p.name}</div>
                                        <div className="text-xs text-slate-500 mt-1">
                                            {p.category} · {p.variants?.length || 0} variantes
                                            {p.availability === "commande"
                                                ? <span data-testid={`admin-mto-${p.slug}`} className="text-[#D97706]"> · Sur commande</span>
                                                : <span className="text-emerald-400"> · En stock</span>}
                                        </div>
                                        <div className="flex gap-2 mt-3">
                                            <button data-testid={`admin-edit-${p.slug}`} onClick={() => setEditing(p)} className="text-xs flex items-center gap-1 text-slate-300 hover:text-white"><Edit2 className="w-3 h-3" /> Modifier</button>
                                            <button data-testid={`admin-delete-${p.slug}`} onClick={() => deleteProduct(p.id)} className="text-xs flex items-center gap-1 text-red-400 hover:text-red-300"><Trash2 className="w-3 h-3" /> Supprimer</button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tab === "commandes" && (
                    <div data-testid="admin-orders-panel">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="font-serif-luxe text-2xl">Commandes</h2>
                            <button data-testid="admin-export-csv-btn" onClick={exportCsv} className="btn-ghost !py-2 !px-4 text-xs"><Download className="w-4 h-4 mr-1" /> CSV</button>
                        </div>
                        <div className="space-y-3">
                            {orders.map((o) => (
                                <div key={o.id} data-testid={`admin-order-${o.order_number}`} className="glass-card rounded-2xl p-4">
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <div>
                                            <div className="font-mono-tight text-sm text-slate-100">{o.order_number}</div>
                                            <div className="text-xs text-slate-500 mt-0.5">{o.email} · {new Date(o.created_at).toLocaleDateString("fr-CA")}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-semibold text-slate-100">{o.total_cad.toFixed(2)} $</div>
                                            <div className="text-xs text-slate-500">{o.payment_provider}</div>
                                        </div>
                                    </div>
                                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-2">
                                        <select
                                            data-testid={`admin-order-status-${o.order_number}`}
                                            value={o.status}
                                            onChange={(e) => updateOrder(o, e.target.value, o.tracking_number, o.tracking_url)}
                                            className="!py-2 text-sm"
                                        >
                                            <option value="pending">En attente</option>
                                            <option value="paid">Payée</option>
                                            <option value="shipped">Expédiée</option>
                                            <option value="delivered">Livrée</option>
                                            <option value="refunded">Remboursée</option>
                                        </select>
                                        <input
                                            data-testid={`admin-order-tracking-${o.order_number}`}
                                            placeholder="N° de suivi"
                                            defaultValue={o.tracking_number || ""}
                                            onBlur={(e) => e.target.value !== (o.tracking_number || "") && updateOrder(o, o.status, e.target.value, o.tracking_url)}
                                            className="!py-2 text-sm"
                                        />
                                        <input
                                            data-testid={`admin-order-tracking-url-${o.order_number}`}
                                            placeholder="URL suivi (optionnel)"
                                            defaultValue={o.tracking_url || ""}
                                            onBlur={(e) => e.target.value !== (o.tracking_url || "") && updateOrder(o, o.status, o.tracking_number, e.target.value)}
                                            className="!py-2 text-sm"
                                        />
                                        <div className={`text-xs flex items-center ${o.payment_status === "paid" ? "text-emerald-400" : "text-slate-500"}`}>
                                            {o.payment_status === "paid" ? "Payée" : "Non payée"}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {orders.length === 0 && <div className="text-slate-500 text-center py-10">Aucune commande.</div>}
                        </div>
                    </div>
                )}

                {tab === "avis" && (
                    <div data-testid="admin-reviews-panel">
                        <h2 className="font-serif-luxe text-2xl mb-6">Modération des avis</h2>
                        <div className="space-y-3">
                            {reviews.map((r) => (
                                <div key={r.id} data-testid={`admin-review-${r.id}`} className="glass-card rounded-2xl p-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-slate-100">{r.author} — {r.rating}/5</div>
                                            <div className="text-sm text-slate-300 mt-1">{r.title}</div>
                                            <div className="text-xs text-slate-500 mt-1">{r.body}</div>
                                        </div>
                                        <div className="flex gap-2">
                                            {!r.approved && (
                                                <button data-testid={`admin-approve-review-${r.id}`} onClick={() => approveReview(r.id)} className="btn-primary !py-2 !px-3 text-xs">Approuver</button>
                                            )}
                                            <button data-testid={`admin-delete-review-${r.id}`} onClick={() => deleteReview(r.id)} className="btn-ghost !py-2 !px-3 text-xs !text-red-400">Supprimer</button>
                                        </div>
                                    </div>
                                    {r.approved && <div className="mt-2 text-[10px] uppercase tracking-widest text-emerald-400">Publié</div>}
                                </div>
                            ))}
                            {reviews.length === 0 && <div className="text-slate-500 text-center py-10">Aucun avis.</div>}
                        </div>
                    </div>
                )}
            </div>

            {/* Product editor modal */}
            {editing && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" data-testid="product-editor-modal">
                    <form onSubmit={saveProduct} className="glass-card rounded-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto space-y-4 my-8">
                        <div className="flex items-center justify-between">
                            <h3 className="font-serif-luxe text-2xl">{editing.id ? "Modifier" : "Nouveau"} produit</h3>
                            <button type="button" data-testid="product-editor-close" onClick={() => setEditing(null)} className="text-slate-400 hover:text-white">Fermer</button>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><label>Nom</label><input data-testid="pe-name" required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
                            <div><label>Slug</label><input data-testid="pe-slug" required value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} /></div>
                        </div>
                        <div><label>Catégorie</label>
                            <select data-testid="pe-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                                <option value="chaines">Chaînes</option>
                                <option value="pendentifs">Pendentifs</option>
                                <option value="bracelets">Bracelets</option>
                                <option value="boucles">Boucles</option>
                            </select>
                        </div>
                        <div><label>Disponibilité</label>
                            <select data-testid="pe-availability" value={editing.availability || "stock"} onChange={(e) => setEditing({ ...editing, availability: e.target.value })}>
                                <option value="stock">En stock — 2-3 jours ouvrables</option>
                                <option value="commande">Sur commande — 2-3 semaines ouvrables</option>
                            </select>
                        </div>
                        <div><label>Description courte</label><input data-testid="pe-short-desc" required value={editing.short_description} onChange={(e) => setEditing({ ...editing, short_description: e.target.value })} /></div>
                        <div><label>Description</label><textarea data-testid="pe-description" rows={4} required value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
                        <div className="flex gap-6">
                            <label className="flex items-center gap-2 text-slate-300 text-sm normal-case">
                                <input data-testid="pe-featured" type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> En avant
                            </label>
                            <label className="flex items-center gap-2 text-slate-300 text-sm normal-case">
                                <input data-testid="pe-active" type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Actif
                            </label>
                        </div>

                        <div>
                            <label>Images</label>
                            <div className="flex flex-wrap gap-3 mt-2">
                                {editing.images.map((img, i) => (
                                    <div key={i} className="relative">
                                        <img src={fileUrl(img)} alt="" className="w-20 h-20 object-cover rounded-lg" />
                                        <button
                                            type="button"
                                            data-testid={`pe-remove-img-${i}`}
                                            onClick={() => setEditing((p) => ({ ...p, images: p.images.filter((_, x) => x !== i) }))}
                                            className="absolute -top-2 -right-2 bg-red-500 rounded-full w-5 h-5 text-xs"
                                        >×</button>
                                    </div>
                                ))}
                                <label data-testid="pe-upload-btn" className="w-20 h-20 border border-dashed border-white/20 rounded-lg flex items-center justify-center cursor-pointer text-slate-500 hover:text-white text-xs">
                                    {uploading ? "…" : "+ Upload"}
                                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                                        const f = e.target.files?.[0];
                                        if (!f) return;
                                        const path = await upload(f);
                                        if (path) setEditing((p) => ({ ...p, images: [...p.images, path] }));
                                    }} />
                                </label>
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label>Variantes</label>
                                <button type="button" data-testid="pe-add-variant-btn" onClick={addVariant} className="text-xs text-slate-300 hover:text-white">+ Ajouter</button>
                            </div>
                            <div className="space-y-2">
                                {editing.variants.map((v, i) => (
                                    <div key={i} className="grid grid-cols-6 gap-2">
                                        <input placeholder="SKU" value={v.sku} onChange={(e) => updateVariant(i, "sku", e.target.value)} className="!py-2 text-xs" />
                                        <input placeholder="Modèle" value={v.model} onChange={(e) => updateVariant(i, "model", e.target.value)} className="!py-2 text-xs" />
                                        <input placeholder="Carats" value={v.carats} onChange={(e) => updateVariant(i, "carats", e.target.value)} className="!py-2 text-xs" />
                                        <input placeholder="Taille" value={v.length_cm} onChange={(e) => updateVariant(i, "length_cm", e.target.value)} className="!py-2 text-xs" />
                                        <input placeholder="Prix" type="number" step="0.01" value={v.price_cad} onChange={(e) => updateVariant(i, "price_cad", e.target.value)} className="!py-2 text-xs" />
                                        <input placeholder="Stock" type="number" value={v.stock} onChange={(e) => updateVariant(i, "stock", e.target.value)} className="!py-2 text-xs" />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button type="button" onClick={() => setEditing(null)} className="btn-ghost">Annuler</button>
                            <button type="submit" data-testid="pe-save-btn" className="btn-primary">Sauvegarder</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
